import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Star, CheckCircle2, Loader2 } from 'lucide-react';
import { withDefaults, backgroundCss, cardCss, fontStack } from '../lib/themes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick relative-luminance check on a #hex color.  Returns true if it's a
// "light" color — we use this to decide whether to wrap an uploaded logo in
// a white card (light theme) or leave it bare (dark theme).
function isLightHex(hex) {
  if (!hex || typeof hex !== 'string') return true;
  const c = hex.replace('#', '');
  if (c.length !== 6) return true;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6;
}

const PublicSurvey = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(null);

  const kioskSeconds = survey?.kiosk_restart_seconds || 0;

  const startNewResponse = () => {
    setResponses({});
    setErrors({});
    setCountdown(null);
    setSubmitted(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Kiosk auto-restart: tick down once per second on the thank-you screen
  useEffect(() => {
    if (!submitted || countdown === null) return;
    if (countdown <= 0) {
      startNewResponse();
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [submitted, countdown]);

  useEffect(() => {
    fetchSurvey();
  }, [surveyId]);

  const fetchSurvey = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/surveys/public/${surveyId}`);
      setSurvey(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Survey not found');
    } finally {
      setLoading(false);
    }
  };

  const updateResponse = (questionId, value) => {
    setResponses({ ...responses, [questionId]: value });
    if (errors[questionId]) {
      setErrors({ ...errors, [questionId]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    survey.questions.forEach(q => {
      if (!q.required) return;
      const v = responses[q.id];
      // Empty / blank: missing key, null, '', or empty array (for checkbox)
      const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
      if (empty) {
        newErrors[q.id] = q.type === 'checkbox'
          ? 'Please select at least one option'
          : 'This field is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/surveys/${surveyId}/submit`, { responses });
      setSubmitted(true);
      if (kioskSeconds > 0) setCountdown(kioskSeconds);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error submitting survey');
    } finally {
      setSubmitting(false);
    }
  };

  // Resolve theme with defaults so older surveys still render
  const theme = withDefaults(survey?.theme || {});
  const primaryColor = theme.primary_color;
  const secondaryColor = theme.secondary_color;
  const backgroundColor = theme.background_color;
  const textColor = theme.text_color;
  const pageBg = backgroundCss(theme);
  const cardStyle = cardCss(theme);
  const font = fontStack(theme.font_family);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ ...pageBg, fontFamily: font }}>
        <Loader2 className="animate-spin" size={32} style={{ color: primaryColor }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ ...pageBg, fontFamily: font }}>
        <div className="text-center">
          <h1 className="text-2xl font-medium mb-2" style={{ color: textColor, fontFamily: font }}>
            Survey Not Available
          </h1>
          <p style={{ color: textColor, opacity: 0.7 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ ...pageBg, fontFamily: font }}>
        <div className="text-center max-w-md animate-fadeIn">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: `${secondaryColor}20` }}
          >
            <CheckCircle2 size={48} style={{ color: secondaryColor }} />
          </div>
          <h2 className="text-2xl font-medium mb-4" style={{ color: textColor, fontFamily: font }}>
            Thank You!
          </h2>
          <p style={{ color: textColor, opacity: 0.7 }}>
            Your response has been submitted successfully. We appreciate your feedback.
          </p>

          {countdown !== null && (
            <p
              className="mt-6 text-sm"
              style={{ color: textColor, opacity: 0.6 }}
              data-testid="kiosk-countdown"
            >
              Returning to the survey in {countdown} second{countdown === 1 ? '' : 's'}…
            </p>
          )}

          <button
            type="button"
            onClick={startNewResponse}
            className="mt-6 px-6 py-3 rounded-lg font-medium transition-opacity hover:opacity-90"
            style={{ backgroundColor: primaryColor, color: '#fff' }}
            data-testid="start-new-response-btn"
          >
            Start New Response
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ ...pageBg, fontFamily: font }}>
      <div className="max-w-2xl mx-auto">
        {/* Logo — wrapped in a white card only on light themes, bare on dark */}
        {theme.logo_url && (
          <img
            src={theme.logo_url.startsWith('http') ? theme.logo_url : `${API_URL}${theme.logo_url}`}
            alt="Logo"
            className={
              isLightHex(theme.background_color)
                ? 'h-16 object-contain mb-8 bg-white rounded-xl p-2 shadow-sm'
                : 'h-16 object-contain mb-8'
            }
          />
        )}

        {/* Header */}
        <div className="mb-10">
          <h1
            className="text-3xl sm:text-4xl font-light mb-4"
            style={{ color: textColor, fontFamily: font }}
          >
            {survey.name}
          </h1>
          {survey.description && (
            <p style={{ color: textColor, opacity: 0.75 }}>{survey.description}</p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="public-survey-form">
          {survey.questions.map((question, index) => (
            <div
              key={question.id}
              className="rounded-2xl p-5 sm:p-6"
              style={cardStyle}
            >
              <QuestionRenderer
                question={question}
                index={index}
                value={responses[question.id]}
                onChange={(value) => updateResponse(question.id, value)}
                error={errors[question.id]}
                theme={{ primaryColor, secondaryColor, textColor, backgroundColor }}
              />
            </div>
          ))}

          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-10 py-4 text-white rounded-full font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 shadow-sm hover:shadow-md"
            style={{ backgroundColor: primaryColor }}
            data-testid="submit-survey-btn"
          >
            {submitting && <Loader2 className="animate-spin" size={20} />}
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

const QuestionRenderer = ({ question, index, value, onChange, error, theme }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const { primaryColor, secondaryColor, textColor, backgroundColor } = theme;

  // Lighten background for inputs
  const inputBg = backgroundColor;
  const borderColor = `${textColor}20`;

  return (
    <div className="space-y-3" data-testid={`survey-question-${index}`}>
      <label className="block text-base font-medium" style={{ color: textColor }}>
        {index + 1}. {question.question}
        {!question.required && <span className="text-sm ml-2" style={{ color: textColor, opacity: 0.5 }}>(optional)</span>}
      </label>

      {question.type === 'radio' && question.options && (
        <div className="flex flex-wrap gap-3">
          {question.options.map(option => (
            <label
              key={option.value}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full border cursor-pointer transition-all"
              style={{
                backgroundColor: value === option.value ? primaryColor : 'white',
                color: value === option.value ? 'white' : textColor,
                borderColor: value === option.value ? primaryColor : borderColor
              }}
              data-testid={`option-${question.id}-${option.value}`}
            >
              <input
                type="radio"
                name={question.id}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              {option.label}
            </label>
          ))}
        </div>
      )}

      {question.type === 'checkbox' && question.options && (
        <div className="flex flex-wrap gap-3" data-testid={`checkbox-group-${question.id}`}>
          {question.options.map(option => {
            const selected = Array.isArray(value) && value.includes(option.value);
            return (
              <label
                key={option.value}
                className="inline-flex items-center justify-center px-6 py-3 rounded-full border cursor-pointer transition-all"
                style={{
                  backgroundColor: selected ? primaryColor : 'white',
                  color: selected ? 'white' : textColor,
                  borderColor: selected ? primaryColor : borderColor
                }}
                data-testid={`checkbox-${question.id}-${option.value}`}
              >
                <input
                  type="checkbox"
                  name={question.id}
                  value={option.value}
                  checked={selected}
                  onChange={() => {
                    const current = Array.isArray(value) ? value : [];
                    onChange(
                      selected
                        ? current.filter(v => v !== option.value)
                        : [...current, option.value]
                    );
                  }}
                  className="sr-only"
                />
                {option.label}
              </label>
            );
          })}
        </div>
      )}

      {question.type === 'star' && (
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="p-1 transition-transform hover:scale-110"
              data-testid={`star-${question.id}-${star}`}
            >
              <Star
                size={36}
                style={{
                  fill: star <= (hoverRating || value) ? secondaryColor : 'none',
                  color: star <= (hoverRating || value) ? secondaryColor : borderColor
                }}
              />
            </button>
          ))}
        </div>
      )}

      {question.type === 'text' && (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 rounded-xl border transition-all focus:outline-none"
          style={{ 
            backgroundColor: inputBg, 
            borderColor,
            color: textColor
          }}
          placeholder={question.placeholder || 'Enter your answer...'}
          data-testid={`text-${question.id}`}
        />
      )}

      {question.type === 'textarea' && (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 rounded-xl border min-h-[120px] resize-y transition-all focus:outline-none"
          style={{ 
            backgroundColor: inputBg, 
            borderColor,
            color: textColor
          }}
          placeholder={question.placeholder || 'Enter your answer...'}
          data-testid={`textarea-${question.id}`}
        />
      )}

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}
    </div>
  );
};

export default PublicSurvey;
